// src/components/DocParticipantsForm.js
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * DocParticipantsForm
 *
 * A reusable, optional people-and-organizations form for Doc Writer.
 *
 * Goals:
 * - Keep the default screen simple.
 * - Let users add only the people or organizations relevant to the document.
 * - Suggest useful roles from the selected category and subject without forcing
 *   a rigid legal-document template.
 * - Support letters, agreements, corporate documents, immigration matters,
 *   litigation, employment documents, real estate documents, estate documents,
 *   government forms, and general business documents.
 *
 * Controlled usage:
 *
 *   const [participants, setParticipants] = useState([]);
 *   const [participantsValid, setParticipantsValid] = useState(true);
 *
 *   <DocParticipantsForm
 *     category={category}
 *     subject={subject}
 *     value={participants}
 *     onChange={setParticipants}
 *     onValidityChange={setParticipantsValid}
 *   />
 *
 * Output shape:
 *
 * [
 *   {
 *     id: "participant_...",
 *     entityType: "person" | "organization",
 *     name: "",
 *     role: "",
 *     title: "",
 *     organizationName: "",
 *     representativeName: "",
 *     contactBlock: "",
 *     details: ""
 *   }
 * ]
 */

const DEFAULT_MAX_PARTICIPANTS = 20;

const BASE_ROLE_SUGGESTIONS = [
  "Sender",
  "Recipient",
  "Applicant",
  "Beneficiary",
  "Petitioner",
  "Respondent",
  "Plaintiff",
  "Defendant",
  "Buyer",
  "Seller",
  "Landlord",
  "Tenant",
  "Employer",
  "Employee",
  "Owner",
  "Member",
  "Managing Member",
  "Manager",
  "Shareholder",
  "Stockholder",
  "Director",
  "Officer",
  "President",
  "Vice President",
  "CEO",
  "COO",
  "CFO",
  "Secretary",
  "Treasurer",
  "Trustee",
  "Grantor",
  "Beneficiary of Trust",
  "Lender",
  "Borrower",
  "Client",
  "Service Provider",
  "Attorney",
  "Representative",
  "Agency",
  "Court",
  "Witness",
  "Dependent",
  "Other",
];

const FAMILY_PRESETS = {
  general: {
    roleSuggestions: [],
  },

  correspondence: {
    roleSuggestions: [
      "Sender",
      "Recipient",
      "Agency",
      "Representative",
    ],
  },

  agreement: {
    roleSuggestions: [
      "First Party",
      "Second Party",
      "Additional Party",
      "Buyer",
      "Seller",
      "Lender",
      "Borrower",
      "Client",
      "Service Provider",
      "Licensor",
      "Licensee",
    ],
  },

  corporate: {
    roleSuggestions: [
      "Company",
      "LLC",
      "Corporation",
      "Nonprofit",
      "Owner",
      "Member",
      "Managing Member",
      "Manager",
      "Shareholder",
      "Stockholder",
      "Director",
      "Officer",
      "President",
      "Vice President",
      "CEO",
      "COO",
      "CFO",
      "Secretary",
      "Treasurer",
      "Registered Agent",
      "Organizer",
      "Incorporator",
    ],
  },

  immigration: {
    roleSuggestions: [
      "Applicant",
      "Petitioner",
      "Beneficiary",
      "Principal Applicant",
      "Derivative Beneficiary",
      "Dependent",
      "Sponsor",
      "Joint Sponsor",
      "Household Member",
      "Employer",
      "Employee",
      "Attorney",
      "Accredited Representative",
      "USCIS",
      "Department of State",
      "Consulate",
      "Embassy",
      "Immigration Court",
    ],
  },

  litigation: {
    roleSuggestions: [
      "Plaintiff",
      "Defendant",
      "Petitioner",
      "Respondent",
      "Appellant",
      "Appellee",
      "Claimant",
      "Debtor",
      "Creditor",
      "Attorney",
      "Witness",
      "Court",
      "Agency",
      "Arbitrator",
      "Mediator",
    ],
  },

  employment: {
    roleSuggestions: [
      "Employer",
      "Employee",
      "Independent Contractor",
      "Supervisor",
      "Manager",
      "Human Resources",
      "Union",
      "Agency",
      "Applicant",
      "Former Employee",
    ],
  },

  realEstate: {
    roleSuggestions: [
      "Owner",
      "Buyer",
      "Seller",
      "Landlord",
      "Tenant",
      "Property Manager",
      "Broker",
      "Agent",
      "Lender",
      "Borrower",
      "HOA",
      "Condominium Association",
      "Housing Authority",
    ],
  },

  estate: {
    roleSuggestions: [
      "Testator",
      "Grantor",
      "Settlor",
      "Trustee",
      "Successor Trustee",
      "Executor",
      "Personal Representative",
      "Administrator",
      "Beneficiary",
      "Heir",
      "Guardian",
      "Conservator",
      "Witness",
      "Notary",
    ],
  },

  family: {
    roleSuggestions: [
      "Spouse",
      "Former Spouse",
      "Parent",
      "Child",
      "Dependent",
      "Petitioner",
      "Respondent",
      "Guardian",
      "Custodian",
      "Attorney",
      "Agency",
    ],
  },

  taxGovernment: {
    roleSuggestions: [
      "Taxpayer",
      "Business Taxpayer",
      "Responsible Party",
      "Authorized Representative",
      "Power of Attorney",
      "Government Agency",
      "IRS",
      "State Tax Agency",
      "Employer",
      "Employee",
      "Payer",
      "Payee",
    ],
  },
};

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function includesAny(text, keywords) {
  return keywords.some((keyword) => text.includes(keyword));
}

function classifyDocumentFamily(category, subject) {
  const categoryText = normalizeText(category);
  const subjectText = normalizeText(subject);
  const combined = `${categoryText} ${subjectText}`;

  if (
    includesAny(combined, [
      "immigration",
      "uscis",
      "visa",
      "naturalization",
      "citizenship",
      "asylum",
      "nvc",
      "consular",
      "i-130",
      "i-485",
      "i-140",
      "i-765",
      "i-864",
      "h-1b",
      "h1b",
      "l-1",
      "o-1",
      "e-2",
      "tn ",
      "daca",
      "tps",
      "sevis",
      "foia",
      "removal",
    ])
  ) {
    return "immigration";
  }

  if (
    includesAny(combined, [
      "business (entity",
      "corporate",
      "corporation",
      "llc",
      "limited liability",
      "operating agreement",
      "bylaw",
      "board resolution",
      "shareholder",
      "stockholder",
      "member resolution",
      "articles of",
      "nonprofit",
      "charit",
      "franchise",
    ])
  ) {
    return "corporate";
  }

  if (
    includesAny(combined, [
      "civil litigation",
      "court procedure",
      "criminal",
      "traffic",
      "bankruptcy",
      "complaint",
      "petition",
      "motion",
      "appeal",
      "hearing",
      "lawsuit",
      "plaintiff",
      "defendant",
      "respondent",
      "subpoena",
      "discovery",
      "arbitration",
      "mediation",
    ])
  ) {
    return "litigation";
  }

  if (
    includesAny(combined, [
      "employment",
      "workplace",
      "workers' compensation",
      "workers compensation",
      "job offer",
      "employee",
      "employer",
      "independent contractor",
      "termination",
      "discipline",
      "wage",
      "harassment",
      "resume",
      "interview",
    ])
  ) {
    return "employment";
  }

  if (
    includesAny(combined, [
      "real estate",
      "landlord",
      "tenant",
      "hoa",
      "property management",
      "housing",
      "mortgage",
      "lease",
      "rental",
      "deed",
      "purchase and sale",
      "property tax",
      "home improvement",
    ])
  ) {
    return "realEstate";
  }

  if (
    includesAny(combined, [
      "estate planning",
      "probate",
      "trust",
      "will",
      "executor",
      "beneficiary designation",
      "power of attorney",
      "advance directive",
      "inheritance",
    ])
  ) {
    return "estate";
  }

  if (
    includesAny(combined, [
      "family law",
      "divorce",
      "custody",
      "child support",
      "spousal support",
      "marriage",
      "parentage",
      "guardianship",
      "adoption",
      "domestic",
    ])
  ) {
    return "family";
  }

  if (
    includesAny(combined, [
      "irs",
      "state tax",
      "taxpayer",
      "tax ",
      "government benefits",
      "ssa",
      "social security",
      "va ",
      "veteran",
      "unemployment",
      "municipal",
      "licensing",
      "permit",
      "public records",
      "government contract",
      "grant",
      "regulator",
      "complaint",
    ])
  ) {
    return "taxGovernment";
  }

  if (
    includesAny(subjectText, [
      "agreement",
      "contract",
      "memorandum of understanding",
      "mou",
      "settlement",
      "release",
      "waiver",
      "terms and conditions",
      "license agreement",
      "service agreement",
    ])
  ) {
    return "agreement";
  }

  if (
    includesAny(subjectText, [
      "letter",
      "notice",
      "request",
      "response",
      "cover",
      "inquiry",
      "follow-up",
      "follow up",
      "demand",
      "statement",
      "declaration",
      "affidavit",
    ])
  ) {
    return "correspondence";
  }

  return "general";
}

function createParticipant({
  role = "",
  entityType = "person",
  name = "",
} = {}) {
  return {
    id: `participant_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    entityType: entityType === "organization" ? "organization" : "person",
    name,
    role,
    title: "",
    organizationName: "",
    representativeName: "",
    contactBlock: "",
    details: "",
  };
}

function normalizeParticipant(raw = {}) {
  return {
    id: String(raw.id || createParticipant().id),
    entityType: raw.entityType === "organization" ? "organization" : "person",
    name: String(raw.name || ""),
    role: String(raw.role || ""),
    title: String(raw.title || ""),
    organizationName: String(raw.organizationName || ""),
    representativeName: String(raw.representativeName || ""),
    contactBlock: String(raw.contactBlock || ""),
    details: String(raw.details || ""),
  };
}

function sameParticipants(left = [], right = []) {
  return JSON.stringify(left) === JSON.stringify(right);
}


function RoleCombobox({
  id,
  value,
  suggestions,
  onChange,
  onBlur,
  disabled = false,
  hasError = false,
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const filteredSuggestions = useMemo(() => {
    const query = normalizeText(filterText);

    if (!query) return suggestions;

    return suggestions.filter((role) =>
      normalizeText(role).includes(query)
    );
  }, [filterText, suggestions]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (activeIndex >= filteredSuggestions.length) {
      setActiveIndex(filteredSuggestions.length ? 0 : -1);
    }
  }, [activeIndex, filteredSuggestions.length]);

  const openMenu = () => {
    if (disabled) return;
    setFilterText("");
    setActiveIndex(-1);
    setOpen(true);
  };

  const chooseRole = (role) => {
    onChange(role);
    setFilterText("");
    setOpen(false);
    setActiveIndex(-1);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (!open) {
        openMenu();
        return;
      }

      setActiveIndex((current) =>
        filteredSuggestions.length
          ? (current + 1 + filteredSuggestions.length) %
            filteredSuggestions.length
          : -1
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      if (!open) {
        openMenu();
        return;
      }

      setActiveIndex((current) =>
        filteredSuggestions.length
          ? (current - 1 + filteredSuggestions.length) %
            filteredSuggestions.length
          : -1
      );
      return;
    }

    if (
      event.key === "Enter" &&
      open &&
      activeIndex >= 0 &&
      filteredSuggestions[activeIndex]
    ) {
      event.preventDefault();
      chooseRole(filteredSuggestions[activeIndex]);
      return;
    }

    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={wrapRef} style={styles.roleCombobox}>
      <div
        style={{
          ...styles.roleInputShell,
          ...(open ? styles.roleInputShellOpen : null),
          ...(hasError ? styles.errorInput : null),
          ...(disabled ? styles.disabledField : null),
        }}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onFocus={openMenu}
          onClick={openMenu}
          onChange={(event) => {
            const nextValue = event.target.value;
            onChange(nextValue);
            setFilterText(nextValue);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Select or type a role"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-menu`}
          aria-activedescendant={
            activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
          }
          style={styles.roleInput}
        />

        <button
          type="button"
          aria-label={open ? "Close role suggestions" : "Open role suggestions"}
          aria-expanded={open}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) {
              setOpen(false);
              setActiveIndex(-1);
            } else {
              openMenu();
              window.requestAnimationFrame(() => inputRef.current?.focus());
            }
          }}
          disabled={disabled}
          style={styles.roleChevronButton}
        >
          <span
            aria-hidden="true"
            style={{
              ...styles.roleChevron,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▾
          </span>
        </button>
      </div>

      {open ? (
        <div
          id={`${id}-menu`}
          role="listbox"
          className="doc-participants-role-menu"
          style={styles.roleMenu}
        >
          {filteredSuggestions.length ? (
            filteredSuggestions.map((role, index) => {
              const selected = normalizeText(role) === normalizeText(value);
              const active = index === activeIndex;

              return (
                <button
                  id={`${id}-option-${index}`}
                  key={role}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => chooseRole(role)}
                  style={{
                    ...styles.roleOption,
                    ...(active ? styles.roleOptionActive : null),
                    ...(selected ? styles.roleOptionSelected : null),
                  }}
                >
                  <span>{role}</span>
                  {selected ? (
                    <span aria-hidden="true" style={styles.roleCheck}>
                      ✓
                    </span>
                  ) : null}
                </button>
              );
            })
          ) : (
            <div style={styles.roleEmpty}>
              Keep typing to use this custom role.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

const DocParticipantsForm = ({
  category = "",
  subject = "",
  value = [],
  onChange,
  onValidityChange,
  disabled = false,
  maxParticipants = DEFAULT_MAX_PARTICIPANTS,
  required = false,
  initialExpanded = false,
}) => {
  const normalizedIncoming = useMemo(
    () => (Array.isArray(value) ? value.map(normalizeParticipant) : []),
    [value]
  );

  const [participants, setParticipants] = useState(normalizedIncoming);
  const [expandedIds, setExpandedIds] = useState(() => {
    if (!initialExpanded) return new Set();
    return new Set(normalizedIncoming.map((item) => item.id));
  });
  const [touched, setTouched] = useState({});
  const participantsRef = useRef(normalizedIncoming);
  const lastEmittedRef = useRef(normalizedIncoming);

  const familyKey = useMemo(
    () => classifyDocumentFamily(category, subject),
    [category, subject]
  );

  const family = FAMILY_PRESETS[familyKey] || FAMILY_PRESETS.general;

  const roleSuggestions = useMemo(
    () =>
      Array.from(
        new Set([
          ...family.roleSuggestions,
          ...BASE_ROLE_SUGGESTIONS,
        ])
      ),
    [family]
  );

  const validity = useMemo(() => {
    if (required && participants.length === 0) return false;

    return participants.every((participant) => {
      const hasName = participant.name.trim().length > 0;
      const hasRole = participant.role.trim().length > 0;
      return hasName && hasRole;
    });
  }, [participants, required]);

  useEffect(() => {
    if (!sameParticipants(normalizedIncoming, lastEmittedRef.current)) {
      participantsRef.current = normalizedIncoming;
      setParticipants(normalizedIncoming);
      lastEmittedRef.current = normalizedIncoming;
    }
  }, [normalizedIncoming]);

  useEffect(() => {
    onValidityChange?.(validity);
  }, [validity, onValidityChange]);

  const emit = (next) => {
    const normalized = next.map(normalizeParticipant);

    // Update the synchronous ref before notifying the parent. This makes
    // repeated mouse, touch, and keyboard changes respond on the first action.
    participantsRef.current = normalized;
    lastEmittedRef.current = normalized;
    setParticipants(normalized);
    onChange?.(normalized);
  };

  const addParticipant = (preset = {}) => {
    const current = participantsRef.current;

    if (disabled || current.length >= maxParticipants) return;

    const participant = createParticipant(preset);
    const next = [...current, participant];
    emit(next);
    setExpandedIds((previous) => {
      const copy = new Set(previous);
      copy.add(participant.id);
      return copy;
    });
  };

  const updateParticipant = (id, patch) => {
    if (disabled) return;

    const current = participantsRef.current;
    const next = current.map((participant) =>
      participant.id === id
        ? { ...participant, ...patch }
        : participant
    );

    emit(next);
  };

  const setParticipantType = (id, entityType) => {
    if (disabled) return;

    const nextType =
      entityType === "organization" ? "organization" : "person";

    const currentParticipant = participantsRef.current.find(
      (participant) => participant.id === id
    );

    if (!currentParticipant || currentParticipant.entityType === nextType) {
      return;
    }

    updateParticipant(id, {
      entityType: nextType,
      ...(nextType === "person"
        ? { representativeName: "" }
        : {}),
    });
  };

  const removeParticipant = (id) => {
    if (disabled) return;

    emit(
      participantsRef.current.filter(
        (participant) => participant.id !== id
      )
    );
    setExpandedIds((previous) => {
      const copy = new Set(previous);
      copy.delete(id);
      return copy;
    });
    setTouched((previous) => {
      const copy = { ...previous };
      delete copy[id];
      return copy;
    });
  };

  const toggleExpanded = (id) => {
    setExpandedIds((previous) => {
      const copy = new Set(previous);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      return copy;
    });
  };

  const markTouched = (id, field) => {
    setTouched((previous) => ({
      ...previous,
      [id]: {
        ...(previous[id] || {}),
        [field]: true,
      },
    }));
  };

  const countLabel =
    participants.length === 0
      ? "No participants added"
      : `${participants.length} participant${
          participants.length === 1 ? "" : "s"
        } added`;

  return (
    <section
      className="doc-participants-form"
      aria-label="Document participants"
      style={styles.section}
    >
      <style>{`
        .doc-participants-form * {
          box-sizing: border-box;
        }

        .doc-participants-form button,
        .doc-participants-form input,
        .doc-participants-form select,
        .doc-participants-form textarea {
          font: inherit;
        }

        .doc-participants-form button:focus-visible,
        .doc-participants-form input:focus-visible,
        .doc-participants-form select:focus-visible,
        .doc-participants-form textarea:focus-visible {
          outline: 3px solid rgba(30, 42, 120, 0.20);
          outline-offset: 2px;
        }

        @media (max-width: 720px) {
          .doc-participants-grid {
            grid-template-columns: 1fr !important;
          }

          .doc-participants-card-header {
            align-items: flex-start !important;
          }

          .doc-participants-card-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .doc-participants-role-menu {
            max-height: min(240px, 42vh) !important;
          }

          .doc-participants-field-help {
            font-size: 10px !important;
          }
        }

        @media (max-width: 520px) {
          .doc-participants-form {
            padding: 12px !important;
          }

          .doc-participants-card-actions {
            justify-content: stretch;
          }

          .doc-participants-card-actions button {
            flex: 1 1 0;
          }
        }
      `}</style>

      <div style={styles.toolbar}>
        <div style={styles.countWrap}>
          <div style={styles.countBadge} aria-live="polite">
            {countLabel}
          </div>

          {subject ? (
            <div style={styles.subjectMeta} title={subject}>
              Suggestions are tailored to the selected document.
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => addParticipant()}
          disabled={disabled || participants.length >= maxParticipants}
          style={{
            ...styles.primaryAddButton,
            ...(disabled || participants.length >= maxParticipants
              ? styles.disabledButton
              : null),
          }}
        >
          <span style={styles.plusIcon} aria-hidden="true">
            +
          </span>
          <span>Add person or organization</span>
        </button>
      </div>

      {participants.length > 0 ? (
        <div style={styles.cardStack}>
          {participants.map((participant, index) => {
            const expanded = expandedIds.has(participant.id);
            const nameTouched = Boolean(touched[participant.id]?.name);
            const roleTouched = Boolean(touched[participant.id]?.role);
            const nameMissing = !participant.name.trim();
            const roleMissing = !participant.role.trim();
            const displayName =
              participant.name.trim() ||
              `Participant ${index + 1}`;
            const displayRole =
              participant.role.trim() ||
              (participant.entityType === "organization"
                ? "Organization"
                : "Person");

            return (
              <article key={participant.id} style={styles.card}>
                <div
                  className="doc-participants-card-header"
                  style={styles.cardHeader}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(participant.id)}
                    style={styles.cardToggle}
                    aria-expanded={expanded}
                    aria-controls={`participant-panel-${participant.id}`}
                  >
                    <span style={styles.cardIndex}>{index + 1}</span>

                    <span style={styles.cardIdentity}>
                      <span style={styles.cardName}>{displayName}</span>
                      <span style={styles.cardRole}>{displayRole}</span>
                    </span>

                    <span style={styles.chevron} aria-hidden="true">
                      {expanded ? "−" : "+"}
                    </span>
                  </button>

                  <div
                    className="doc-participants-card-actions"
                    style={styles.cardActions}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(participant.id)}
                      style={styles.smallButton}
                    >
                      {expanded ? "Collapse" : "Edit"}
                    </button>

                    <button
                      type="button"
                      onClick={() => removeParticipant(participant.id)}
                      disabled={disabled}
                      style={{
                        ...styles.removeButton,
                        ...(disabled ? styles.disabledButton : null),
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div
                    id={`participant-panel-${participant.id}`}
                    style={styles.cardBody}
                  >
                    <div
                      className="doc-participants-grid"
                      style={styles.grid}
                    >
                      <Field label="Participant type" required>
                        <div
                          role="group"
                          aria-label="Participant type"
                          style={styles.typeToggle}
                        >
                          <button
                            type="button"
                            aria-pressed={participant.entityType === "person"}
                            onPointerDown={(event) => {
                              if (
                                typeof event.button === "number" &&
                                event.button !== 0
                              ) {
                                return;
                              }

                              event.preventDefault();
                              setParticipantType(participant.id, "person");
                            }}
                            onClick={() =>
                              setParticipantType(participant.id, "person")
                            }
                            disabled={disabled}
                            style={{
                              ...styles.typeToggleButton,
                              ...(participant.entityType === "person"
                                ? styles.typeToggleButtonActive
                                : null),
                              ...(disabled ? styles.disabledField : null),
                            }}
                          >
                            <span
                              style={{
                                ...styles.typeToggleDot,
                                ...(participant.entityType === "person"
                                  ? styles.typeToggleDotActive
                                  : null),
                              }}
                              aria-hidden="true"
                            />
                            <span>Person</span>
                          </button>

                          <button
                            type="button"
                            aria-pressed={
                              participant.entityType === "organization"
                            }
                            onPointerDown={(event) => {
                              if (
                                typeof event.button === "number" &&
                                event.button !== 0
                              ) {
                                return;
                              }

                              event.preventDefault();
                              setParticipantType(
                                participant.id,
                                "organization"
                              );
                            }}
                            onClick={() =>
                              setParticipantType(
                                participant.id,
                                "organization"
                              )
                            }
                            disabled={disabled}
                            style={{
                              ...styles.typeToggleButton,
                              ...(participant.entityType === "organization"
                                ? styles.typeToggleButtonActive
                                : null),
                              ...(disabled ? styles.disabledField : null),
                            }}
                          >
                            <span
                              style={{
                                ...styles.typeToggleDot,
                                ...(participant.entityType === "organization"
                                  ? styles.typeToggleDotActive
                                  : null),
                              }}
                              aria-hidden="true"
                            />
                            <span>Organization</span>
                          </button>
                        </div>
                      </Field>

                      <Field
                        label={
                          participant.entityType === "organization"
                            ? "Organization name"
                            : "Full name"
                        }
                        required
                        error={
                          nameTouched && nameMissing
                            ? "Please enter a name."
                            : ""
                        }
                      >
                        <input
                          type="text"
                          value={participant.name}
                          onChange={(event) =>
                            updateParticipant(participant.id, {
                              name: event.target.value,
                            })
                          }
                          onBlur={() =>
                            markTouched(participant.id, "name")
                          }
                          disabled={disabled}
                          placeholder={
                            participant.entityType === "organization"
                              ? "Business, agency, court, association, or organization"
                              : "Person's full legal or commonly used name"
                          }
                          style={{
                            ...styles.input,
                            ...(nameTouched && nameMissing
                              ? styles.errorInput
                              : null),
                          }}
                        />
                      </Field>

                      <Field
                        label="Role in this document"
                        required
                        help="Select a suggestion or type any role."
                        error={
                          roleTouched && roleMissing
                            ? "Please enter this participant's role."
                            : ""
                        }
                      >
                        <RoleCombobox
                          id={`participant-role-${participant.id}`}
                          value={participant.role}
                          suggestions={roleSuggestions}
                          onChange={(role) =>
                            updateParticipant(participant.id, { role })
                          }
                          onBlur={() =>
                            markTouched(participant.id, "role")
                          }
                          disabled={disabled}
                          hasError={roleTouched && roleMissing}
                        />
                      </Field>

                      {participant.entityType === "person" ? (
                        <>
                          <Field
                            label="Title or position"
                            help="Optional"
                          >
                            <input
                              type="text"
                              value={participant.title}
                              onChange={(event) =>
                                updateParticipant(participant.id, {
                                  title: event.target.value,
                                })
                              }
                              disabled={disabled}
                              placeholder="Managing Member, President, Attorney, etc."
                              style={styles.input}
                            />
                          </Field>

                          <Field
                            label="Organization or company"
                            help="Optional"
                          >
                            <input
                              type="text"
                              value={participant.organizationName}
                              onChange={(event) =>
                                updateParticipant(participant.id, {
                                  organizationName: event.target.value,
                                })
                              }
                              disabled={disabled}
                              placeholder="Organization this person represents"
                              style={styles.input}
                            />
                          </Field>
                        </>
                      ) : (
                        <Field
                          label="Representative or contact person"
                          help="Optional"
                        >
                          <input
                            type="text"
                            value={participant.representativeName}
                            onChange={(event) =>
                              updateParticipant(participant.id, {
                                representativeName: event.target.value,
                              })
                            }
                            disabled={disabled}
                            placeholder="Name and title of a representative"
                            style={styles.input}
                          />
                        </Field>
                      )}

                      <Field
                        label="Contact or address information"
                        help="Optional"
                        fullWidth
                      >
                        <textarea
                          value={participant.contactBlock}
                          onChange={(event) =>
                            updateParticipant(participant.id, {
                              contactBlock: event.target.value,
                            })
                          }
                          disabled={disabled}
                          placeholder="Mailing address, email, phone, department, agency office, or other contact information"
                          style={styles.textarea}
                        />
                      </Field>

                      <Field
                        label="Important details about this participant"
                        help="Optional"
                        fullWidth
                      >
                        <textarea
                          value={participant.details}
                          onChange={(event) =>
                            updateParticipant(participant.id, {
                              details: event.target.value,
                            })
                          }
                          disabled={disabled}
                          placeholder="Ownership percentage, relationship, authority, responsibilities, identifying facts, or special instructions"
                          style={styles.textarea}
                        />
                      </Field>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon} aria-hidden="true">
            +
          </div>
          <div>
            <div style={styles.emptyTitle}>
              No participants have been added
            </div>
            <div style={styles.emptyText}>
              Add only the people or organizations needed for this document.
            </div>
          </div>
        </div>
      )}

      <div style={styles.footerRow}>
        <div style={styles.footerMessage}>
          {participants.length >= maxParticipants
            ? `The maximum of ${maxParticipants} participants has been reached.`
            : `${Math.max(
                0,
                maxParticipants - participants.length
              )} additional participant${
                maxParticipants - participants.length === 1 ? "" : "s"
              } may be added.`}
        </div>

        {!validity ? (
          <div role="alert" style={styles.validationMessage}>
            Complete the required name and role fields for each added participant.
          </div>
        ) : null}
      </div>
    </section>
  );
};

function Field({
  label,
  required = false,
  help = "",
  error = "",
  fullWidth = false,
  children,
}) {
  return (
    <div
      style={{
        ...styles.field,
        ...(fullWidth ? styles.fullWidth : null),
      }}
    >
      <div style={styles.fieldLabelRow}>
        <label style={styles.label}>
          {label}
          {required ? <span style={styles.required}>*</span> : null}
        </label>

        {help ? (
          <span
            className="doc-participants-field-help"
            style={styles.inlineHelp}
            title={help}
          >
            {help}
          </span>
        ) : null}
      </div>

      {children}

      {error ? <div style={styles.errorText}>{error}</div> : null}
    </div>
  );
}

const styles = {
  section: {
    width: "100%",
    border: "1px solid rgba(15, 23, 42, 0.12)",
    borderRadius: 16,
    background: "#FFFFFF",
    padding: 18,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  countWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 9,
    flexWrap: "wrap",
  },

  countBadge: {
    flex: "0 0 auto",
    border: "1px solid rgba(30, 42, 120, 0.18)",
    borderRadius: 999,
    background: "#F4F6FF",
    color: "#1E2A78",
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 850,
  },

  subjectMeta: {
    minWidth: 0,
    color: "#64748B",
    fontSize: 11.5,
    lineHeight: 1.4,
    fontWeight: 700,
  },

  primaryAddButton: {
    minHeight: 42,
    border: "1px solid #1E2A78",
    borderRadius: 10,
    background: "#1E2A78",
    color: "#FFFFFF",
    padding: "9px 13px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: "0 7px 16px rgba(30, 42, 120, 0.16)",
  },

  plusIcon: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
  },

  disabledButton: {
    cursor: "not-allowed",
    opacity: 0.55,
    boxShadow: "none",
  },

  cardStack: {
    marginTop: 14,
    display: "grid",
    gap: 12,
  },

  card: {
    overflow: "visible",
    border: "1px solid rgba(15, 23, 42, 0.12)",
    borderRadius: 14,
    background: "#FFFFFF",
  },

  cardHeader: {
    minHeight: 68,
    padding: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: "#FBFCFF",
  },

  cardToggle: {
    minWidth: 0,
    flex: "1 1 auto",
    border: 0,
    background: "transparent",
    padding: 4,
    display: "flex",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
    cursor: "pointer",
  },

  cardIndex: {
    width: 34,
    height: 34,
    flex: "0 0 34px",
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#E9EDFF",
    color: "#1E2A78",
    fontWeight: 900,
    fontSize: 13,
  },

  cardIdentity: {
    minWidth: 0,
    flex: "1 1 auto",
    display: "grid",
    gap: 2,
  },

  cardName: {
    overflow: "hidden",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 900,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  cardRole: {
    overflow: "hidden",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  chevron: {
    color: "#1E2A78",
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 700,
  },

  cardActions: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  smallButton: {
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 9,
    background: "#FFFFFF",
    color: "#334155",
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
  },

  removeButton: {
    border: "1px solid rgba(185, 28, 28, 0.18)",
    borderRadius: 9,
    background: "#FFF7F7",
    color: "#B91C1C",
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 850,
  },

  cardBody: {
    borderTop: "1px solid rgba(15, 23, 42, 0.09)",
    padding: 14,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 13,
  },

  field: {
    minWidth: 0,
  },

  fullWidth: {
    gridColumn: "1 / -1",
  },

  fieldLabelRow: {
    width: "100%",
    minWidth: 0,
    minHeight: 22,
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "nowrap",
  },

  label: {
    minWidth: 0,
    margin: 0,
    color: "#0F172A",
    fontSize: 13,
    lineHeight: 1.25,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  required: {
    marginLeft: 3,
    color: "#DC2626",
  },

  inlineHelp: {
    minWidth: 0,
    marginLeft: "auto",
    color: "#64748B",
    fontSize: 10.5,
    lineHeight: 1.25,
    fontWeight: 700,
    whiteSpace: "nowrap",
    textAlign: "right",
  },

  input: {
    width: "100%",
    minHeight: 43,
    border: "1px solid rgba(15, 23, 42, 0.16)",
    borderRadius: 10,
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "10px 11px",
    fontSize: 14,
    outline: "none",
  },

  typeToggle: {
    width: "100%",
    minHeight: 43,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 4,
    padding: 4,
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 10,
    background: "#F8FAFC",
    touchAction: "manipulation",
    userSelect: "none",
  },

  typeToggleButton: {
    minWidth: 0,
    minHeight: 35,
    border: "1px solid transparent",
    borderRadius: 7,
    background: "transparent",
    color: "#475569",
    padding: "7px 8px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    fontSize: 12.5,
    lineHeight: 1.2,
    fontWeight: 850,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    transition:
      "background 90ms ease, border-color 90ms ease, color 90ms ease, box-shadow 90ms ease, transform 90ms ease",
  },

  typeToggleButtonActive: {
    borderColor: "#1E2A78",
    background: "#1E2A78",
    color: "#FFFFFF",
    boxShadow: "0 3px 9px rgba(30, 42, 120, 0.18)",
  },

  typeToggleDot: {
    width: 7,
    height: 7,
    flex: "0 0 7px",
    borderRadius: 999,
    border: "1px solid currentColor",
    background: "transparent",
  },

  typeToggleDotActive: {
    borderColor: "#FFFFFF",
    background: "#FFFFFF",
  },

  disabledField: {
    cursor: "not-allowed",
    opacity: 0.58,
  },

  roleCombobox: {
    position: "relative",
    width: "100%",
    zIndex: 15,
  },

  roleInputShell: {
    width: "100%",
    minHeight: 43,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 42px",
    alignItems: "stretch",
    border: "1px solid rgba(15, 23, 42, 0.16)",
    borderRadius: 10,
    background: "#FFFFFF",
    overflow: "hidden",
    transition:
      "border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
  },

  roleInputShellOpen: {
    borderColor: "#1E2A78",
    boxShadow: "0 0 0 3px rgba(30, 42, 120, 0.10)",
  },

  roleInput: {
    width: "100%",
    minWidth: 0,
    border: 0,
    background: "transparent",
    color: "#0F172A",
    padding: "10px 11px",
    fontSize: 14,
    outline: "none",
  },

  roleChevronButton: {
    width: 42,
    minWidth: 42,
    border: 0,
    borderLeft: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#F8FAFC",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },

  roleChevron: {
    display: "inline-block",
    fontSize: 16,
    lineHeight: 1,
    transition: "transform 140ms ease",
  },

  roleMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 50,
    maxHeight: 220,
    overflowY: "auto",
    overscrollBehavior: "contain",
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 11,
    background: "#FFFFFF",
    padding: 5,
    boxShadow:
      "0 18px 38px rgba(15, 23, 42, 0.16), 0 3px 10px rgba(15, 23, 42, 0.08)",
    scrollbarWidth: "thin",
  },

  roleOption: {
    width: "100%",
    minHeight: 36,
    border: 0,
    borderRadius: 8,
    background: "transparent",
    color: "#334155",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    textAlign: "left",
    fontSize: 12.5,
    lineHeight: 1.3,
    fontWeight: 750,
  },

  roleOptionActive: {
    background: "#F1F5FF",
    color: "#1E2A78",
  },

  roleOptionSelected: {
    color: "#1E2A78",
    fontWeight: 900,
  },

  roleCheck: {
    color: "#1E2A78",
    fontSize: 13,
    fontWeight: 950,
  },

  roleEmpty: {
    padding: "10px 11px",
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.45,
  },

  textarea: {
    width: "100%",
    minHeight: 92,
    border: "1px solid rgba(15, 23, 42, 0.16)",
    borderRadius: 10,
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "10px 11px",
    fontSize: 14,
    lineHeight: 1.45,
    resize: "vertical",
    outline: "none",
  },

  errorInput: {
    borderColor: "#DC2626",
    boxShadow: "0 0 0 2px rgba(220, 38, 38, 0.08)",
  },

  errorText: {
    marginTop: 5,
    color: "#B91C1C",
    fontSize: 12,
    fontWeight: 700,
  },

  emptyState: {
    marginTop: 14,
    border: "1px dashed rgba(30, 42, 120, 0.24)",
    borderRadius: 14,
    background: "#FCFDFF",
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  emptyIcon: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#E9EDFF",
    color: "#1E2A78",
    fontSize: 22,
    fontWeight: 800,
  },

  emptyTitle: {
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
  },

  emptyText: {
    marginTop: 2,
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.45,
  },

  footerRow: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },

  footerMessage: {
    color: "#64748B",
    fontSize: 11,
    lineHeight: 1.4,
  },

  validationMessage: {
    color: "#B91C1C",
    fontSize: 12,
    fontWeight: 800,
  },
};

export default DocParticipantsForm;
